import React, { useState, useEffect, useCallback } from "react";
import {
  Stamp, Check, X, Clock, RefreshCw, User,
  Mail, Send, MessageSquare, Calendar, AlertTriangle, ChevronDown
} from "lucide-react";

const API = getApiUrl();
import getApiUrl from '../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const ACTION_META = {
  send_email: { label: "Envoyer email", icon: Mail, color: "text-cyan-400" },
  send_sms: { label: "Envoyer SMS", icon: Send, color: "text-green-400" },
  send_whatsapp: { label: "Envoyer WhatsApp", icon: MessageSquare, color: "text-emerald-400" },
  create_rdv: { label: "Creer RDV", icon: Calendar, color: "text-blue-400" },
  modify_status: { label: "Modifier statut", icon: RefreshCw, color: "text-amber-400" },
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    approved: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
  };
  const labels = { pending: "En attente", approved: "Approuve", rejected: "Rejete" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs border ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function VisaIAPanel({ authUser }) {
  const [tab, setTab] = useState("pending"); // pending | history
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, hRes] = await Promise.all([
        authFetch(`${API}/api/visa-ia/pending`),
        authFetch(`${API}/api/visa-ia/history?limit=30`)
      ]);
      if (pRes.ok) setPending((await pRes.json()).actions || []);
      if (hRes.ok) setHistory((await hRes.json()).actions || []);
    } catch (e) {
      console.error("Erreur chargement Visa IA:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (visaId) => {
    setActing(visaId);
    try {
      const resp = await authFetch(`${API}/api/visa-ia/${visaId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewer: authUser?.email || "admin" })
      });
      if (resp.ok) load();
    } catch (e) { console.error(e); }
    setActing(null);
  };

  const reject = async (visaId) => {
    setActing(visaId);
    try {
      const resp = await authFetch(`${API}/api/visa-ia/${visaId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewer: authUser?.email || "admin", reason: rejectReason })
      });
      if (resp.ok) { setRejectReason(""); setExpandedId(null); load(); }
    } catch (e) { console.error(e); }
    setActing(null);
  };

  const items = tab === "pending" ? pending : history;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="visa-loading">
        <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
        <span className="ml-2 text-slate-400">Chargement Visa IA...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="visa-ia-panel">
      {/* Sub tabs */}
      <div className="flex items-center gap-3 mb-2">
        <button
          data-testid="visa-tab-pending"
          onClick={() => setTab("pending")}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
            tab === "pending" ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
          }`}
        >
          <Clock className="w-4 h-4" /> En attente ({pending.length})
        </button>
        <button
          data-testid="visa-tab-history"
          onClick={() => setTab("history")}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
            tab === "history" ? "bg-slate-600/40 text-slate-300 border border-slate-500/30" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
          }`}
        >
          <Check className="w-4 h-4" /> Historique ({history.length})
        </button>
        <div className="flex-1" />
        <button onClick={load} className="p-2 hover:bg-slate-700 rounded-lg" data-testid="visa-refresh-btn">
          <RefreshCw className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {/* Empty state */}
      {items.length === 0 && (
        <div className="text-center py-16 bg-slate-900/50 rounded-xl border border-slate-800" data-testid="visa-empty">
          <Stamp className="w-14 h-14 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 font-medium">
            {tab === "pending" ? "Aucune action en attente de validation" : "Aucun historique"}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {tab === "pending" ? "Les actions IA avec Visa apparaitront ici" : "Les decisions passees apparaitront ici"}
          </p>
        </div>
      )}

      {/* Action cards */}
      <div className="space-y-3">
        {items.map(action => {
          const meta = ACTION_META[action.action_type] || { label: action.action_type, icon: AlertTriangle, color: "text-slate-400" };
          const ActionIcon = meta.icon;
          const isExpanded = expandedId === action.visa_id;

          return (
            <div key={action.visa_id} data-testid={`visa-card-${action.visa_id}`} className="bg-slate-900/50 rounded-xl border border-slate-700 overflow-hidden">
              {/* Card header */}
              <div className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${meta.color} bg-current/10`}>
                  <ActionIcon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{meta.label}</span>
                    <StatusBadge status={action.status} />
                    {action.target && <span className="text-xs text-slate-400">vers {action.target}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                    <User className="w-3 h-3" /> {action.agent_name}
                    <span className="text-slate-600">|</span>
                    <Clock className="w-3 h-3" /> {formatDate(action.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {action.status === "pending" && (
                    <>
                      <button
                        data-testid={`approve-${action.visa_id}`}
                        onClick={() => approve(action.visa_id)}
                        disabled={acting === action.visa_id}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-lg text-sm flex items-center gap-1"
                      >
                        {acting === action.visa_id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approuver
                      </button>
                      <button
                        data-testid={`reject-${action.visa_id}`}
                        onClick={() => setExpandedId(isExpanded ? null : action.visa_id)}
                        className="px-3 py-1.5 bg-red-600/80 hover:bg-red-500 rounded-lg text-sm flex items-center gap-1"
                      >
                        <X className="w-3.5 h-3.5" /> Rejeter
                      </button>
                    </>
                  )}
                  <button onClick={() => setExpandedId(isExpanded ? null : action.visa_id)} className="p-1.5 hover:bg-slate-700 rounded">
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                  </button>
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="border-t border-slate-700 p-4 space-y-3 bg-slate-800/20">
                  {action.context && (
                    <div><span className="text-xs text-slate-400 font-medium">Contexte :</span><p className="text-sm text-slate-300 mt-0.5">{action.context}</p></div>
                  )}
                  {action.content && Object.keys(action.content).length > 0 && (
                    <div>
                      <span className="text-xs text-slate-400 font-medium">Contenu :</span>
                      <pre className="mt-1 p-3 bg-slate-900 rounded-lg text-xs text-slate-300 overflow-auto max-h-48">{JSON.stringify(action.content, null, 2)}</pre>
                    </div>
                  )}
                  {action.status === "rejected" && action.rejection_reason && (
                    <div><span className="text-xs text-red-400 font-medium">Raison du rejet :</span><p className="text-sm text-red-300 mt-0.5">{action.rejection_reason}</p></div>
                  )}
                  {action.reviewed_by && (
                    <div className="text-xs text-slate-500">Decide par {action.reviewed_by} le {formatDate(action.reviewed_at)}</div>
                  )}
                  {/* Reject form */}
                  {action.status === "pending" && (
                    <div className="flex items-center gap-2 pt-2 border-t border-slate-700/50">
                      <input
                        data-testid={`reject-reason-${action.visa_id}`}
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder="Raison du rejet (optionnel)..."
                        className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-red-500/50"
                      />
                      <button
                        data-testid={`confirm-reject-${action.visa_id}`}
                        onClick={() => reject(action.visa_id)}
                        disabled={acting === action.visa_id}
                        className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded-lg text-sm"
                      >
                        Confirmer rejet
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
